/**
 * PreservedReadOnly — wraps a premium feature page when the account is a
 * DOWNGRADED former-premium account now on Free Weekly.
 *
 * Per the owner directive: when a former Premium / Business / Government
 * account falls back to Free Weekly we must NOT delete or hide their setup.
 * We show the saved configuration READ-ONLY with a clear "paused" banner,
 * while disabling editing / saving / running premium automation. The backend
 * already keeps write endpoints at 403 (can_edit=false), so this layer makes
 * the UI itself read-only and explains the state.
 */
import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { PauseCircle, ArrowRight } from 'lucide-react';

const PAUSED_MESSAGE = {
  en: 'This configuration is saved but paused on the Free Weekly plan. Restore your plan to edit settings and resume automation.',
  ja: 'この設定は保存されていますが、無料週次プランでは一時停止されています。設定の編集や自動処理を再開するには、プランを再開してください。',
};

/** The paused-config banner. Reusable on its own where a full read-only
 *  wrapper isn't needed (e.g. an inline section). */
export function PausedConfigBanner({ testId = 'paused-config-banner' }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  return (
    <div
      className="rounded-2xl border-2 border-amber-200 bg-amber-50/60 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      data-testid={testId}
    >
      <div className="flex items-start gap-3 min-w-0">
        <span className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
          <PauseCircle className="w-5 h-5 text-amber-700" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">
            {ja ? '設定は保存済み・一時停止中' : 'Saved configuration · Paused'}
          </p>
          <p className="text-[13px] text-slate-700 mt-0.5 leading-relaxed" data-testid="paused-config-message">
            {ja ? PAUSED_MESSAGE.ja : PAUSED_MESSAGE.en}
          </p>
        </div>
      </div>
      <Link
        to="/dashboard/plan?intent=restore-premium"
        className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
        data-testid="paused-config-restore-btn"
      >
        {ja ? 'プランを再開' : 'Restore plan'}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

/**
 * Wrap a feature page's children. Renders the paused banner, then the page
 * content inside an `inert`, pointer-events-disabled region so nothing can be
 * edited, saved, or triggered. The banner (with the Restore CTA) stays fully
 * interactive because it sits OUTSIDE the inert region.
 */
export default function PreservedReadOnly({ children, testId = 'preserved-readonly' }) {
  const regionRef = useRef(null);
  useEffect(() => {
    // `inert` removes the subtree from tab order + blocks all interaction.
    // Set imperatively for cross-browser/React-version safety.
    if (regionRef.current) regionRef.current.inert = true;
  }, []);
  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        <PausedConfigBanner />
      </div>
      <div
        ref={regionRef}
        className="pointer-events-none opacity-70 select-none"
        aria-disabled="true"
        data-testid="preserved-readonly-region"
      >
        {children}
      </div>
    </div>
  );
}
